import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useCreateCredential, useUpdateCredential } from '@/hooks/useCredentials';
import type { Credential } from '@/types/api.types';

const createSchema = z.object({
  label: z.string().min(3, 'Mínimo 3 caracteres'),
  apiToken: z.string().min(20, 'Token deve ter no mínimo 20 caracteres'),
  senderId: z.string().min(1, 'Sender ID obrigatório'),
  isActive: z.boolean(),
});

const editSchema = z.object({
  label: z.string().min(3, 'Mínimo 3 caracteres'),
  apiToken: z
    .string()
    .refine((v) => v === '' || v.length >= 20, 'Token deve ter no mínimo 20 caracteres'),
  senderId: z.string().min(1, 'Sender ID obrigatório'),
  isActive: z.boolean(),
});

type Values = z.infer<typeof createSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credential?: Credential | null;
}

export function CredentialFormDialog({ open, onOpenChange, credential }: Props) {
  const isEdit = !!credential;
  const [showToken, setShowToken] = useState(false);

  const { mutate: create, isPending: creating } = useCreateCredential();
  const { mutate: update, isPending: updating } = useUpdateCredential();
  const isPending = creating || updating;

  const form = useForm<Values>({
    resolver: zodResolver(isEdit ? editSchema : createSchema),
    defaultValues: { label: '', apiToken: '', senderId: '', isActive: true },
  });

  useEffect(() => {
    if (open) {
      setShowToken(false);
      form.reset({
        label: credential?.label ?? '',
        apiToken: '',
        senderId: credential?.senderId ?? '',
        isActive: credential?.isActive ?? true,
      });
    }
  }, [open, credential, form]);

  function onSubmit(values: Values) {
    if (isEdit && credential) {
      const data: Parameters<typeof update>[0]['data'] = {
        label: values.label,
        senderId: values.senderId,
        isActive: values.isActive,
      };
      if (values.apiToken) data.apiToken = values.apiToken;
      update({ id: credential.id, data }, { onSuccess: () => onOpenChange(false) });
    } else {
      create(
        { label: values.label, apiToken: values.apiToken, senderId: values.senderId, isActive: values.isActive },
        { onSuccess: () => onOpenChange(false) }
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar credencial' : 'Nova credencial'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label</FormLabel>
                  <FormControl>
                    <Input placeholder="Produção" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="apiToken"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Token Zenvia{isEdit && <span className="ml-1 text-xs text-muted-foreground">(deixe vazio para manter)</span>}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showToken ? 'text' : 'password'}
                        placeholder={isEdit ? '••••••••••••••••' : 'Cole o token aqui'}
                        className="pr-10"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-10 w-10"
                        onClick={() => setShowToken((s) => !s)}
                        tabIndex={-1}
                      >
                        {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="senderId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sender ID</FormLabel>
                  <FormControl>
                    <Input placeholder="disparador" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="isActive"
                      />
                    </FormControl>
                    <Label htmlFor="isActive">Ativa</Label>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
